/**
 * What the monitor concluded about one call's audio, for the record and its
 * export.
 *
 * Composed here rather than asked of `VoipMonitorService`, out of its public
 * `getMetricsHistory()` and its own exported pure helpers. The service's job is
 * to feed the live connection-quality indicator; the diagnostics panel is a
 * second reader with a different question, and giving it its own summary keeps
 * that asymmetry out of the service.
 *
 * Every field is optional because each depends on the browser having reported
 * the underlying stat at least once. A call that failed before media flowed has
 * none of them, and a zero there would read as "perfect" rather than "never
 * measured".
 */

import { isCodecReport } from 'Services/voipMonitorService/guards';
import type { CollectedStats } from 'Services/voipMonitorService/index.types';
import {
  calculateMetricAverage,
  calculateMosScore,
} from 'Services/voipMonitorService/utils';

export interface CallQualityCodec {
  name: string;
  clockRate?: number;
  channels?: number;
}

export interface CallQualitySummary {
  /** ITU-T G.107 E-Model, 1.0 (unusable) to 5.0 (perfect). */
  mosScore?: number;
  /** Mean inbound jitter in seconds, across every sample. */
  averageJitter?: number;
  /** Inbound packets lost over the whole call. */
  totalPacketsLost?: number;
  /** Mean round-trip time in seconds, across every sample. */
  averageRoundTripTime?: number;
  /** Last reported bitrate the selected candidate pair could carry, in bps. */
  availableBitrate?: number;
  /** How many samples the averages are drawn from. */
  sampleCount: number;
  codec?: CallQualityCodec;
}

/**
 * Inbound packets lost over the whole call.
 *
 * `packetsLost` is cumulative since the call started, so the total is the
 * largest value seen, not the sum of the samples. The maximum rather than the
 * last one because the counter is per-SSRC and restarts if the stream is
 * renegotiated mid-call — undercounting after a renegotiation is a smaller lie
 * than dropping back to zero.
 */
const readTotalPacketsLost = (
  metrics: CollectedStats[]
): number | undefined => {
  const counts = metrics
    .map((metric) => metric.basicStats?.packetsLost)
    .filter((value): value is number => typeof value === 'number');

  return counts.length > 0 ? Math.max(...counts) : undefined;
};

/** Capacity, not throughput — the last figure the candidate pair reported. */
const readAvailableBitrate = (
  metrics: CollectedStats[]
): number | undefined => {
  for (let index = metrics.length - 1; index >= 0; index -= 1) {
    const bitrate = metrics[index].basicStats?.availableBitrate;

    if (typeof bitrate === 'number') {
      return bitrate;
    }
  }

  return undefined;
};

/** What the two ends actually agreed on — PCMU and Opus fail differently. */
const readCodec = (metrics: CollectedStats[]): CallQualityCodec | undefined => {
  for (let index = metrics.length - 1; index >= 0; index -= 1) {
    const codec = metrics[index].raw?.find(isCodecReport);

    if (codec?.mimeType) {
      return {
        name: codec.mimeType.split('/')[1] ?? codec.mimeType,
        clockRate: codec.clockRate,
        channels: codec.channels,
      };
    }
  }

  return undefined;
};

export const buildCallQualitySummary = (
  metrics: CollectedStats[]
): CallQualitySummary | null => {
  if (metrics.length === 0) {
    return null;
  }

  return {
    mosScore: calculateMosScore(metrics),
    averageJitter: calculateMetricAverage(
      metrics,
      (metric) => metric.basicStats?.jitter
    ),
    totalPacketsLost: readTotalPacketsLost(metrics),
    averageRoundTripTime: calculateMetricAverage(
      metrics,
      (metric) => metric.basicStats?.roundTripTime
    ),
    availableBitrate: readAvailableBitrate(metrics),
    sampleCount: metrics.length,
    codec: readCodec(metrics),
  };
};
